import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Turkish mobile: optional +90/0 prefix then 5XXXXXXXXX (10 digits starting with 5)
const TR_PHONE_RE = /^(?:\+90|0)?5\d{9}$/;

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", branch: "", password: "", passwordConfirmation: "", gender: "", dob: "" });
  const [errors, setErrors] = useState({});
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { document.title = "Get Up Fit | Kayıt Ol"; }, []);
  useEffect(() => { api.get("/branches/public").then((r) => setBranches(r.data)).catch(() => setBranches([])); }, []);

  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.fullName.trim() || form.fullName.trim().length < 2) e.fullName = "Ad soyad gerekli (en az 2 karakter).";
    if (!form.email.trim()) e.email = "E-posta adresi gerekli.";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Geçerli bir e-posta adresi girin.";
    const phoneClean = form.phone.replace(/[\s()-]/g, "");
    if (!phoneClean) e.phone = "Telefon numarası gerekli.";
    else if (!TR_PHONE_RE.test(phoneClean)) e.phone = "Geçerli bir Türkiye cep numarası girin (05XX XXX XX XX).";
    if (!form.branch) e.branch = "Lütfen bir şube seçin.";
    if (!form.password) e.password = "Şifre gerekli.";
    else if (form.password.length < 8) e.password = "Şifre en az 8 karakter olmalı.";
    if (!form.passwordConfirmation) e.passwordConfirmation = "Şifre tekrarı gerekli.";
    else if (form.password !== form.passwordConfirmation) e.passwordConfirmation = "Şifreler eşleşmiyor.";
    if (!consent) e.consent = "Devam etmek için onay vermelisiniz.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!validate()) return;
    setSaving(true);
    try {
      await api.post("/auth/register", {
        name: form.fullName.trim(), email: form.email.trim(), phone: form.phone.trim(), password: form.password,
        branch_id: form.branch, gender: form.gender || null, dob: form.dob || null, consent: true,
      });
      toast.success("Kaydın oluşturuldu! Giriş yapılıyor...");
      await login(form.email.trim(), form.password);
      navigate("/member");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      if (/e-posta zaten/i.test(msg)) setErrors((p) => ({ ...p, email: msg }));
      toast.error(msg || "Kayıt başarısız. Lütfen tekrar deneyin.");
    } finally { setSaving(false); }
  };

  const errCls = (k) => `bg-[#0E0E14] border-[#2D2D3F] text-white mt-1 ${errors[k] ? "border-red-500/70 focus-visible:ring-red-500" : ""}`;
  const Err = ({ k }) => errors[k] ? <p id={`reg-${k}-err`} data-testid={`reg-${k}-error`} className="text-xs text-red-400 mt-1">{errors[k]}</p> : null;

  return (
    <div className="min-h-screen bg-[#0B0B0E] flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-md" data-testid="register-page">
        <div className="flex justify-center mb-6"><Logo /></div>
        <div className="rounded-2xl border border-[#222230] bg-[#13131C] p-6 sm:p-8">
          <h1 className="font-display font-extrabold text-2xl text-white mb-1 flex items-center gap-2"><UserPlus size={22} className="text-[#FF5500]" /> Yeni Üye Ol</h1>
          <p className="text-sm text-[#68687B] mb-5">Birkaç adımda hesabını oluştur.</p>
          <form onSubmit={submit} noValidate className="space-y-3">
            <div>
              <Label htmlFor="reg-fullName" className="text-xs text-[#A0A0B2]">Ad Soyad</Label>
              <Input id="reg-fullName" name="fullName" autoComplete="name" value={form.fullName}
                onChange={(e) => set("fullName", e.target.value)} data-testid="reg-name"
                aria-invalid={!!errors.fullName} aria-describedby={errors.fullName ? "reg-fullName-err" : undefined} className={errCls("fullName")} />
              <Err k="fullName" />
            </div>
            <div>
              <Label htmlFor="reg-email" className="text-xs text-[#A0A0B2]">E-posta</Label>
              <Input id="reg-email" name="email" type="email" autoComplete="email" value={form.email}
                onChange={(e) => set("email", e.target.value)} data-testid="reg-email"
                aria-invalid={!!errors.email} aria-describedby={errors.email ? "reg-email-err" : undefined} className={errCls("email")} />
              <Err k="email" />
            </div>
            <div>
              <Label htmlFor="reg-phone" className="text-xs text-[#A0A0B2]">Telefon</Label>
              <Input id="reg-phone" name="phone" type="tel" autoComplete="tel" value={form.phone}
                onChange={(e) => set("phone", e.target.value)} data-testid="reg-phone" placeholder="05XX XXX XX XX"
                aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "reg-phone-err" : undefined} className={errCls("phone")} />
              <Err k="phone" />
            </div>
            <div>
              <Label htmlFor="reg-branch" className="text-xs text-[#A0A0B2]">Şube</Label>
              <Select value={form.branch} onValueChange={(v) => set("branch", v)}>
                <SelectTrigger id="reg-branch" name="branch" data-testid="reg-branch"
                  aria-invalid={!!errors.branch} className={errCls("branch")}><SelectValue placeholder="Şube seç" /></SelectTrigger>
                <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white">
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Err k="branch" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="reg-password" className="text-xs text-[#A0A0B2]">Şifre</Label>
                <Input id="reg-password" name="password" type="password" autoComplete="new-password" value={form.password}
                  onChange={(e) => set("password", e.target.value)} data-testid="reg-password"
                  aria-invalid={!!errors.password} aria-describedby={errors.password ? "reg-password-err" : "reg-password-hint"} className={errCls("password")} />
                <Err k="password" />
              </div>
              <div>
                <Label htmlFor="reg-passwordConfirmation" className="text-xs text-[#A0A0B2]">Şifre (Tekrar)</Label>
                <Input id="reg-passwordConfirmation" name="passwordConfirmation" type="password" autoComplete="new-password" value={form.passwordConfirmation}
                  onChange={(e) => set("passwordConfirmation", e.target.value)} data-testid="reg-confirm"
                  aria-invalid={!!errors.passwordConfirmation} aria-describedby={errors.passwordConfirmation ? "reg-passwordConfirmation-err" : undefined} className={errCls("passwordConfirmation")} />
                <Err k="passwordConfirmation" />
              </div>
            </div>
            <p id="reg-password-hint" className="text-xs text-[#68687B]">Şifre en az 8 karakter olmalıdır.</p>

            <div className="flex items-start gap-2.5 pt-1">
              <Checkbox id="reg-consent" checked={consent} data-testid="reg-consent"
                onCheckedChange={(v) => { setConsent(!!v); if (errors.consent) setErrors((p) => ({ ...p, consent: undefined })); }}
                className="mt-0.5 border-[#2D2D3F] data-[state=checked]:bg-[#FF5500] data-[state=checked]:border-[#FF5500]" />
              <Label htmlFor="reg-consent" className="text-xs text-[#A0A0B2] leading-relaxed cursor-pointer">
                <Link to="/kvkk" target="_blank" rel="noopener noreferrer" className="text-[#FF5500] hover:underline">KVKK Aydınlatma Metni</Link>,{" "}
                <Link to="/gizlilik" target="_blank" rel="noopener noreferrer" className="text-[#FF5500] hover:underline">Gizlilik Politikası</Link> ve{" "}
                <Link to="/kosullar" target="_blank" rel="noopener noreferrer" className="text-[#FF5500] hover:underline">Kullanım Koşulları</Link>'nı okudum ve onaylıyorum.
              </Label>
            </div>
            <Err k="consent" />

            <Button type="submit" disabled={saving} data-testid="reg-submit" className="w-full bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold mt-2">
              {saving ? <Loader2 className="animate-spin" size={16} /> : "Kayıt Ol"}
            </Button>
          </form>
          <p className="text-center text-sm text-[#68687B] mt-4">Zaten hesabın var mı? <Link to="/login" data-testid="reg-login-link" className="text-[#FF5500] hover:underline">Giriş Yap</Link></p>
        </div>
      </div>
    </div>
  );
}
